/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

// Composables
import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';

import Home from '@/views/Home.vue';

// BAR
import NavbarBack from '@/views/NavbarBack.vue';

// PAGES
import NewsLayout from '@/views/news/layout.vue';
import News from '@/views/news/page.vue';
import NewsPage from '@/views/news/[id]/page.vue';
import MoreLayout from '@/views/more/layout.vue';
import More from '@/views/more/page.vue';

import Opinion from '@/views/opinion/page.vue';
import OpinionLayout from '@/views/opinion/layout.vue';
import OpinionPage from '@/views/opinion/[id]/page.vue';

import TV from '@/views/tv24h/page.vue';
import TVLayout from '@/views/tv24h/layout.vue';

import Search from '@/views/search/page.vue';
import Newsletter from '@/views/more/newsletter/page.vue';
import Notifications from '@/views/more/notifications/page.vue';
import About from '@/views/more/about/page.vue';
import Contact from '@/views/more/contact/page.vue';
import Legal from '@/views/more/legal/page.vue';
import { fetchPostByUrl } from '@/api/posts';
import categories from '@/configs/categories';
import { useAppStore } from '@/store/app';

const routes = [
    {
        path: '/',
        component: Home,
        name: 'Home',
        meta: {
            authRequired: false,
        },
        redirect: { name: 'NewsLayout' },
        children: [
            {
                path: 'news',
                component: NewsLayout,
                name: 'NewsLayout',
                redirect: { name: 'News', params: { category: 'news' } },
                children: [
                    {
                        path: ':category?',
                        name: 'News',
                        component: News,
                        meta: {
                            title: 'App.title',
                        },
                    },
                    {
                        path: 'post/:id',
                        name: 'NewsPage',
                        component: NewsPage,
                        meta: {
                            navbar: NavbarBack,
                        },
                    },
                    {
                        path: 'opinion/:id',
                        name: 'OpinionPage',
                        component: OpinionPage,
                        meta: {
                            navbar: NavbarBack,
                        },
                    },
                ],
            },

            {
                path: 'more',
                component: MoreLayout,
                name: 'More',
                redirect: { name: 'MorePage' },
                children: [
                    {
                        path: '',
                        name: 'MorePage',
                        component: More,
                        meta: {
                            title: 'pages.more.title',
                        },
                    },
                    {
                        path: 'newsletter',
                        name: 'Newsletter',
                        component: Newsletter,
                        meta: {
                            title: 'pages.newsletter.title',
                            navbar: NavbarBack,
                        },
                    },
                    {
                        path: 'notifications',
                        name: 'Notifications',
                        component: Notifications,
                        meta: {
                            title: 'pages.notifications.title',
                            navbar: NavbarBack,
                        },
                    },
                    {
                        path: 'about',
                        name: 'About',
                        component: About,
                        meta: {
                            title: 'pages.about.title',
                            navbar: NavbarBack,
                        },
                    },
                    {
                        path: 'contact',
                        name: 'Contact',
                        component: Contact,
                        meta: {
                            title: 'pages.contact.title',
                            navbar: NavbarBack,
                        },
                    },
                    {
                        path: 'legal',
                        name: 'Legal',
                        component: Legal,
                        meta: {
                            title: 'pages.legal.title',
                            navbar: NavbarBack,
                        },
                    },
                ],
            },
            {
                path: 'tv24h',
                name: 'TVLayout',
                component: TVLayout,
                meta: {
                    title: 'categories.tv24h',
                },
                redirect: { name: 'TV24h' },
                children: [
                    {
                        path: '',
                        name: 'TV24h',
                        component: TV,
                    },
                ],
            },
            {
                path: 'search',
                name: 'Search',
                component: Search,
                meta: {
                    title: 'pages.search.title',
                },
            },
        ],
    },
    // other not found routes, redirect to the home page
    {
        path: '/:pathMatch(.*)*',
        redirect: { name: 'Home' },
    },
] as Readonly<RouteRecordRaw[]>;

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

router.onError((err) => {
    console.error('Router error:', err);
});

App.addListener('appUrlOpen', async function (event: URLOpenListenerEvent) {
    const appStore = useAppStore();
    try {
        if (event.url.includes('app.24horas.pt/news/post/')) {
            // get last path from the url
            const lastPath = event.url.split('/').pop();
            if (lastPath) {
                router.push({
                    name: 'NewsPage',
                    params: { id: lastPath },
                });
            }
        } else {
            appStore.setDeepLink(true);
            const response = await fetchPostByUrl(event.url);
            if (response.data && response.data.post_id) {
                // check if response.data.categories has any id of the categories
                if (response.data.post_type === 'post') {
                    // If the post is found, redirect to the NewsPage with the post ID
                    router.push({
                        name: 'NewsPage',
                        params: { id: response.data.post_id },
                        query: {
                            f: 'follow',
                        },
                    });
                } else if (response.data.post_type === 'opinion') {
                    // If the post is found, redirect to the NewsPage with the post ID
                    router.push({
                        name: 'OpinionPage',
                        params: { id: response.data.post_id },
                        query: {
                            f: 'follow',
                        },
                    });
                } else {
                    console.error('Post not found in the categories:', response.data.post_id);
                    appStore.setInvalidDeepLink(true);
                }
            } else {
                console.error('Post not found:', response.data);
                appStore.setInvalidDeepLink(true);
            }
        }
    } catch (error: any) {
        console.error('Error fetching post by URL:', error);
        appStore.setInvalidDeepLink(true);
    } finally {
        appStore.setDeepLink(false);
    }
});

export default router;
